import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTimerDuration } from "../services/services";

export const secondsToTimeParts = (totalSeconds: number) => {
  const clamped = Math.max(0, Math.floor(totalSeconds));

  const days = Math.floor(clamped / (24 * 60 * 60));
  const hours = Math.floor((clamped % (24 * 60 * 60)) / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;

  return { days, hours, minutes, seconds };
};

export const useTimerFetch = () => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [noContent, setIsNoContent] = useState(false);
  const deadlineRef = useRef<number | null>(null);
  const displayedSecondRef = useRef<number>(-1);

  const syncFromClock = useCallback(() => {
    const deadline = deadlineRef.current;
    if (deadline === null) {
      return;
    }

    const seconds = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    if (seconds === displayedSecondRef.current) {
      return;
    }

    displayedSecondRef.current = seconds;
    setRemainingSeconds(seconds);
  }, []);

  const applySeconds = useCallback((seconds: number) => {
    if (seconds <= 0) {
      deadlineRef.current = null;
      displayedSecondRef.current = 0;
      setRemainingSeconds(0);
      return;
    }

    deadlineRef.current = Date.now() + seconds * 1000;
    displayedSecondRef.current = seconds;
    setRemainingSeconds(seconds);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;

    async function fetchTimer() {
      try {
        const ns = await fetchTimerDuration();
        if (cancelled) {
          return;
        }

        if (ns === false) {
          deadlineRef.current = null;
          displayedSecondRef.current = -1;
          setFailed(true);
          setIsNoContent(true);
        } else {
          applySeconds(Math.floor(Number(ns) / 1_000_000_000));
          setFailed(false);
          setIsNoContent(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    const wake = () => {
      syncFromClock();
      void fetchTimer();
    };

    const onFrame = () => {
      syncFromClock();
      rafId = requestAnimationFrame(onFrame);
    };

    void fetchTimer();
    rafId = requestAnimationFrame(onFrame);

    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("pageshow", wake);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("pageshow", wake);
    };
  }, [applySeconds, syncFromClock]);

  return { remainingSeconds, loading, failed, noContent };
};
